import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SubmitButton } from '../../../components/mui/Buttons/Buttons';
import {
  bulkUpdateSkillxIds,
  getSkillxStudents,
  getSkillxStudentsSummary,
} from '../../../redux/features/skillxSlice';

/**
 * Bulk SkillX IDs — upload a sheet of admission number → SkillX ID pairs.
 *
 * Choosing a file sends it to the server as a DRY RUN straight away, so the
 * errors, and the students whose existing ID the file would replace, are on
 * screen before anything is written. Save is all or nothing: one bad row and
 * the server writes none of them. The checks live on the server only — it is
 * the one that knows who is a SkillX student and who already holds an ID.
 *
 * Only the first sheet is read, and its two columns are found by heading rather
 * than position, so a sheet with extra columns (a student name to work from)
 * uploads just as well.
 */

const SAMPLE_FILE = 'SkillxIdUploadStructure.xlsx';

// Heading → field, compared with case, spaces and punctuation removed, so
// "Adm No", "admn_no" and "Admission Number" all find the same column.
const COLUMNS = {
  admn_no: { label: 'Admission No', aliases: ['admissionno', 'admissionnumber', 'admnno', 'admno'] },
  skillx_id: { label: 'SkillX ID', aliases: ['skillxid', 'skillx'] },
};

const normaliseHeading = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * What the cell shows, as text. A number cell keeps its display text only while
 * that is plain digits — so a zero-padded format ("00123") survives — and
 * otherwise uses the stored value, so a long number in General format never
 * arrives as "1.23457E+11".
 */
const cellText = (cell) => {
  if (!cell || cell.v == null) return '';
  const shown = cell.w != null ? String(cell.w).trim() : '';
  if (cell.t === 'n') return /^\d+$/.test(shown) ? shown : String(cell.v);
  return shown || String(cell.v).trim();
};

/** Read the first sheet into [{ row, admn_no, skillx_id }]. Throws a message a person can act on. */
async function readSkillxSheet(file) {
  let workbook;
  try {
    workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  } catch (error) {
    throw new Error('This file could not be read. Upload an .xlsx or .xls spreadsheet.');
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet || !sheet['!ref']) throw new Error('The first sheet in this file is empty.');

  const range = XLSX.utils.decode_range(sheet['!ref']);
  const textAt = (r, c) => cellText(sheet[XLSX.utils.encode_cell({ r, c })]);

  // The heading row is the first of the top ten that has BOTH headings, so a
  // title line above the table is stepped over rather than read as the header.
  let headingRow = -1;
  let columnOf = {};
  for (let r = range.s.r; r <= Math.min(range.e.r, range.s.r + 9) && headingRow < 0; r += 1) {
    const found = {};
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const heading = normaliseHeading(textAt(r, c));
      Object.entries(COLUMNS).forEach(([field, { aliases }]) => {
        if (found[field] === undefined && aliases.includes(heading)) found[field] = c;
      });
    }
    if (Object.keys(found).length === Object.keys(COLUMNS).length) {
      headingRow = r;
      columnOf = found;
    }
  }

  if (headingRow < 0) {
    throw new Error(
      `Couldn't find the "${COLUMNS.admn_no.label}" and "${COLUMNS.skillx_id.label}" columns. Use the downloaded structure, or keep those two headings.`,
    );
  }

  const rows = [];
  for (let r = headingRow + 1; r <= range.e.r; r += 1) {
    const admnNo = textAt(r, columnOf.admn_no);
    const skillxId = textAt(r, columnOf.skillx_id);
    // `row` is the line number Excel shows, so an error can be found in the file.
    if (admnNo || skillxId) rows.push({ row: r + 1, admn_no: admnNo, skillx_id: skillxId });
  }

  if (!rows.length) throw new Error('The sheet has the headings but no rows under them.');
  return rows;
}

function downloadSampleSheet() {
  const sheet = XLSX.utils.aoa_to_sheet([[COLUMNS.admn_no.label, COLUMNS.skillx_id.label]]);
  sheet['!cols'] = [{ wch: 18 }, { wch: 24 }];

  const notes = XLSX.utils.aoa_to_sheet([
    ['How to fill the "SkillX IDs" sheet'],
    [COLUMNS.admn_no.label, "The student's ERP admission number. The student must be enrolled on a SkillX course."],
    [COLUMNS.skillx_id.label, 'Exactly as issued, up to 30 characters. Scanner punches are matched on it.'],
    ['', 'One row per student. An admission number or a SkillX ID may appear only once in the file.'],
    ['', 'If the IDs start with zeros, format the SkillX ID column as Text before typing them.'],
    ['', 'A student who already has a different SkillX ID gets the one in the file; they are listed before you save.'],
    ['', 'If any row is wrong, nothing is saved. Fix the rows listed and upload the file again.'],
    ['', 'Only the first sheet is read, up to 2000 rows. To clear an ID, use the table one student at a time.'],
  ]);
  notes['!cols'] = [{ wch: 18 }, { wch: 100 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'SkillX IDs');
  XLSX.utils.book_append_sheet(workbook, notes, 'Instructions');
  XLSX.writeFile(workbook, SAMPLE_FILE);
}

/** A small scrolling table for the check results. The first cell of each row is its sheet row number. */
function ResultTable({ head, rows }) {
  return (
    <TableContainer
      sx={{ maxHeight: 280, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {head.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells) => (
            <TableRow key={cells[0]}>
              {cells.map((cell, i) => (
                <TableCell key={head[i]} sx={{ fontSize: 12.5 }}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function BulkUploadSkillxIds({ dispatchparams }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);

  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [rows, setRows] = useState([]);
  // The server's last answer for `rows`: { ok, message, data }.
  const [check, setCheck] = useState(null);

  const fileInputRef = useRef(null);
  // Picking a second file before the first one's check returns must not let the
  // older answer land on top of the newer file.
  const pickSeq = useRef(0);

  const reset = () => {
    pickSeq.current += 1;
    setRows([]);
    setCheck(null);
    setFileError('');
    setReading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const upload = (payloadRows, dryRun) =>
    dispatch(bulkUpdateSkillxIds({ rows: payloadRows, dryRun })).then((res) => ({
      ok: res.payload?.status === 'success',
      message: res.payload?.message || 'Could not reach the server. Try again.',
      data: res.payload?.data || {},
    }));

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    const seq = ++pickSeq.current;
    setRows([]);
    setCheck(null);
    setFileError('');
    if (!file) return;

    setReading(true);
    try {
      const parsed = await readSkillxSheet(file);
      if (seq !== pickSeq.current) return;
      setRows(parsed);
      const result = await upload(parsed, true);
      if (seq !== pickSeq.current) return;
      setCheck(result);
    } catch (error) {
      if (seq === pickSeq.current) setFileError(error.message);
    } finally {
      if (seq === pickSeq.current) setReading(false);
    }
  };

  const handleSave = async () => {
    const result = await upload(rows, false);
    if (result.ok) {
      enqueueSnackbar(result.message, { variant: 'success' });
      dispatch(getSkillxStudents(dispatchparams));
      dispatch(
        getSkillxStudentsSummary({
          fromDate: dispatchparams?.fromDate,
          toDate: dispatchparams?.toDate,
        }),
      );
      handleClose();
    } else {
      // Something changed since the check — say an ID was given out from the
      // table in the meantime. Show the new problems where the old plan was.
      setCheck(result);
      enqueueSnackbar(result.message, { variant: 'error' });
    }
  };

  const plan = check?.ok ? check.data : null;
  const rowErrors = check && !check.ok ? check.data?.errors || [] : [];
  const hasChanges = Boolean(plan) && plan.to_add + plan.to_replace > 0;

  return (
    <GlobalDialog
      title="Bulk Upload SkillX IDs"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="md"
      closeButton
      closeCondition={handleClose}
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Bulk Upload IDs
        </AddButton>
      }
      content={
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            One row per student: the admission number and the SkillX ID to give them. The file is
            checked as soon as you choose it. Nothing is saved until you press Save, and if any row
            is wrong, nothing is saved at all.
          </Typography>

          <Box>
            <Button size="small" variant="outlined" onClick={downloadSampleSheet}>
              <Icon icon="vscode-icons:file-type-excel2" width="25" height="25" />
              <Typography ml="4px" variant="button">
                Download Excel Structure
              </Typography>
            </Button>
          </Box>

          <TextField
            type="file"
            label="Select an Excel file to upload"
            InputLabelProps={{ shrink: true }}
            inputRef={fileInputRef}
            inputProps={{ accept: '.xlsx,.xls' }}
            onChange={handleFileChange}
            disabled={loading && !reading}
            error={Boolean(fileError)}
            helperText={fileError || (reading ? 'Checking the file...' : '')}
          />

          {check && !check.ok && <Alert severity="error">{check.message}</Alert>}

          {rowErrors.length > 0 && (
            <ResultTable
              head={['Row', 'Admission No', 'SkillX ID', 'Problem']}
              rows={rowErrors.map((e) => [
                e.row,
                e.student_name ? `${e.admn_no} — ${e.student_name}` : e.admn_no || '—',
                e.skillx_id || '—',
                e.errors.join('; '),
              ])}
            />
          )}

          {plan && (
            <Alert severity={hasChanges ? 'success' : 'info'}>
              {plan.total} {plan.total === 1 ? 'row' : 'rows'} checked: <b>{plan.to_add}</b> new, <b>{plan.to_replace}</b>{' '}
              replacing an existing ID, <b>{plan.unchanged}</b> already up to date.
            </Alert>
          )}

          {plan?.replaced?.length > 0 && (
            <>
              <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                These students already have a different SkillX ID. Saving replaces it. Anything
                already recorded against the old ID stays where it is.
              </Alert>
              <ResultTable
                head={['Row', 'Admission No', 'Student', 'Current ID', 'New ID']}
                rows={plan.replaced.map((r) => [
                  r.row,
                  r.admn_no,
                  r.student_name || '—',
                  r.from,
                  r.to,
                ])}
              />
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Stack direction="row" spacing={1}>
              <CancelButton size="small" action={handleClose}>
                Cancel
              </CancelButton>
              <SubmitButton
                size="small"
                disabled={!hasChanges || loading || reading}
                action={handleSave}
              >
                {loading && !reading ? 'Please wait...' : 'Save'}
              </SubmitButton>
            </Stack>
          </Box>
        </Stack>
      }
    />
  );
}
